import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, LogOut, Play } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/site/Logo";
import { useReveal } from "@/hooks/useReveal";
import {
  clearToken,
  requestAccess,
  verifyAccess,
} from "@/lib/membersAuth";
import { LIBRARY_VIDEOS } from "@/data/biblioteca";
import { trackEvent } from "@/lib/analytics";

function Gate({ onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim()) {
      setError("Introduza o seu código de acesso.");
      return;
    }
    setLoading(true);
    try {
      const data = await requestAccess(code.trim());
      trackEvent("members_access_granted", { member_id: data.member_id });
      toast("Bem-vinda(o) · Biblioteca Lu.ft");
      onSuccess(data);
    } catch (err) {
      const status = err?.response?.status;
      trackEvent("members_access_denied", { status: status || "network" });
      setError(
        status === 401
          ? "Código não reconhecido. Verifique a mensagem que recebeu."
          : "Não foi possível validar o acesso. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative min-h-screen flex flex-col bg-[var(--luft-bg)]"
      data-testid="members-gate"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, var(--luft-bg) 0%, var(--luft-bg-alt) 60%, var(--luft-bg) 100%)",
          }}
        />
        <div
          className="absolute -top-32 -right-20 w-[55vw] h-[55vw] rounded-full opacity-[0.28]"
          style={{ background: "radial-gradient(circle, var(--luft-sky) 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -left-24 w-[50vw] h-[50vw] rounded-full opacity-[0.18]"
          style={{ background: "radial-gradient(circle, var(--luft-blue) 0%, transparent 72%)" }}
        />
      </div>

      {/* Header */}
      <header className="relative px-6 sm:px-10 lg:px-16 pt-10 pb-6 flex items-center justify-between">
        <button onClick={() => navigate("/")} aria-label="Lu.ft início">
          <Logo />
        </button>
        <button
          onClick={() => navigate("/online")}
          className="inline-flex items-center gap-2 text-[var(--luft-ink-soft)] text-[0.7rem] uppercase tracking-[0.28em] hover:text-[var(--luft-blue)] transition-colors"
          data-testid="members-gate-back"
        >
          <ArrowLeft size={14} strokeWidth={1.4} />
          Lu.ft Online
        </button>
      </header>

      <div className="relative flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16">
        <div className="w-full max-w-[520px]">
          <div className="text-center mb-12">
            <span
              className="inline-flex items-center gap-2 text-[var(--luft-ink-soft)] mb-8"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "0.62rem",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
              }}
            >
              <Lock size={11} strokeWidth={1.5} />
              Área privada
            </span>
            <h1 className="editorial-heading text-[clamp(2.2rem,5.4vw,3.6rem)] text-[var(--luft-ink)] leading-[1]">
              Biblioteca<br />
              <em className="editorial-italic">Lu.ft</em>
            </h1>
            <p className="mt-7 font-body font-light text-[var(--luft-ink-soft)] text-[0.97rem] leading-relaxed">
              Introduza o código de acesso pessoal que recebeu na inscrição
              como aluna(o) do estúdio Lu.ft.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-7" data-testid="members-gate-form">
            <div>
              <label
                htmlFor="members-code"
                className="block text-[var(--luft-ink-soft)] mb-3"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "0.6rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
              >
                Código de acesso
              </label>
              <input
                id="members-code"
                type="text"
                autoComplete="off"
                spellCheck="false"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="LUFT-VERAO-•••"
                className="w-full bg-transparent border-b border-[var(--luft-ink)]/35 px-1 py-3.5 text-[var(--luft-ink)] text-center tracking-[0.18em] placeholder:text-[var(--luft-ink)]/25 focus:outline-none focus:border-[var(--luft-blue)] transition-colors"
                style={{ fontFamily: "Montserrat, sans-serif", fontSize: "1rem" }}
                data-testid="members-gate-input"
              />
            </div>

            {error && (
              <p
                className="text-center text-[var(--luft-blue)]"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "0.72rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
                data-testid="members-gate-error"
              >
                {error}
              </p>
            )}

            <div className="flex justify-center pt-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-[var(--luft-ink)] text-[#F6F4EE] rounded-full text-[0.72rem] uppercase tracking-[0.24em] hover:bg-[var(--luft-blue)] transition-colors disabled:opacity-60"
                data-testid="members-gate-submit"
              >
                {loading ? "A validar..." : "Entrar"}
                <ArrowRight size={14} strokeWidth={1.4} />
              </button>
            </div>
          </form>

          <p
            className="mt-14 text-center text-[var(--luft-ink-soft)]"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.66rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
          >
            Ainda não é aluna(o) do estúdio?{" "}
            <button
              onClick={() => navigate("/#aulas")}
              className="text-[var(--luft-ink)] underline underline-offset-4 hover:text-[var(--luft-blue)] transition-colors"
              data-testid="members-gate-join"
            >
              Conhecer as aulas
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}

function MemberVideoCard({ video, index, onPlay }) {
  return (
    <article
      className="reveal group"
      data-testid={`member-video-${video.id}`}
    >
      <button
        type="button"
        onClick={() => onPlay(video)}
        className="relative block w-full aspect-[4/5] overflow-hidden bg-[var(--luft-bg-alt)] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--luft-blue)]"
        style={{ borderRadius: "var(--radius-organic)" }}
        data-testid={`member-video-play-${video.id}`}
      >
        <img
          src={video.poster}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
          style={{ filter: "grayscale(0.35) contrast(1.02)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/55" />

        <span
          className="absolute top-6 left-6 font-display italic text-2xl text-white/85"
          aria-hidden="true"
        >
          {["i", "ii", "iii", "iv"][index] || ""}
        </span>

        <span className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/55 bg-white/8 backdrop-blur-md flex items-center justify-center transition-all duration-700 group-hover:bg-white group-hover:border-white">
          <Play
            size={14}
            strokeWidth={1.2}
            className="text-white ml-0.5 transition-colors duration-500 group-hover:text-[var(--luft-ink)]"
            fill="currentColor"
          />
        </span>

        <span className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
          <span>
            <span
              className="block text-white/65"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "0.58rem",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
              }}
            >
              {video.teacher}
            </span>
            <span className="block font-display italic text-white text-xl mt-1.5">
              {video.duration}
            </span>
          </span>
          <span
            className="text-white/72"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.6rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            {video.level}
          </span>
        </span>
      </button>

      <div className="mt-7">
        <h3 className="editorial-heading text-2xl sm:text-[1.6rem] text-[var(--luft-ink)] leading-tight">
          {video.title}
        </h3>
        <p className="mt-4 text-[var(--luft-ink-soft)] font-body font-light text-[0.93rem] leading-relaxed max-w-sm">
          {video.description}
        </p>
      </div>
    </article>
  );
}

function VideoPlayerModal({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!video) return null;
  const hasVimeo = !!video.vimeoId;

  return (
    <div
      className="fixed inset-0 z-50 bg-[var(--luft-ink)]/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
      role="dialog"
      aria-modal="true"
      data-testid="member-video-modal"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 sm:top-10 sm:right-10 text-[#F6F4EE] text-[0.7rem] uppercase tracking-[0.28em] border-b border-[#F6F4EE]/55 pb-1.5 hover:text-[var(--luft-yellow)] hover:border-[var(--luft-yellow)] transition-colors"
        data-testid="member-video-modal-close"
      >
        Fechar
      </button>

      <div className="w-full max-w-[1100px]">
        <p
          className="text-[#F6F4EE]/55 mb-4"
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "0.6rem",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
          }}
        >
          {video.duration} · {video.level}
        </p>
        <h3 className="editorial-heading text-[#F6F4EE] text-[clamp(1.8rem,3.2vw,2.4rem)] mb-8">
          {video.title}
        </h3>

        <div className="relative w-full aspect-video bg-black overflow-hidden">
          {hasVimeo ? (
            <iframe
              src={`https://player.vimeo.com/video/${video.vimeoId}?title=0&byline=0&portrait=0&dnt=1`}
              title={video.title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid={`member-video-iframe-${video.id}`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center px-8">
              <div className="text-center max-w-md">
                <p
                  className="text-[#F6F4EE]/55 mb-4"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "0.62rem",
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                  }}
                >
                  Vídeo em preparação
                </p>
                <p className="font-display italic text-[#F6F4EE] text-2xl sm:text-3xl leading-snug">
                  "Esta aula será adicionada<br />em breve à sua biblioteca."
                </p>
                <p className="mt-6 text-[#F6F4EE]/55 text-sm font-body font-light">
                  Receberá um aviso quando estiver disponível.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Library({ member, onLogout }) {
  const ref = useReveal();
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const playVideo = (video) => {
    setActive(video);
    trackEvent("member_video_open", { video_id: video.id });
  };

  return (
    <main className="bg-[var(--luft-bg)]" ref={ref} data-testid="members-library">
      {/* Header */}
      <header className="px-6 sm:px-10 lg:px-16 pt-10 pb-4 flex items-center justify-between border-b border-[var(--luft-line)]">
        <button onClick={() => navigate("/")} aria-label="Lu.ft início">
          <Logo />
        </button>
        <div className="flex items-center gap-6">
          <span
            className="hidden sm:inline-block text-[var(--luft-ink-soft)]"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.62rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
            data-testid="members-id"
          >
            {member.member_id}
          </span>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-[var(--luft-ink)] text-[0.7rem] uppercase tracking-[0.28em] hover:text-[var(--luft-blue)] transition-colors"
            data-testid="members-logout"
          >
            <LogOut size={13} strokeWidth={1.4} />
            Sair
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 sm:px-10 lg:px-16 pt-20 pb-8 max-w-[1440px] mx-auto">
        <p className="eyebrow mb-8">Biblioteca Lu.ft · área privada</p>
        <h1 className="editorial-heading text-[clamp(2.6rem,7vw,5.6rem)] text-[var(--luft-ink)] leading-[0.94] max-w-4xl">
          A sua prática,<br />
          <em className="editorial-italic">à sua medida</em>.
        </h1>
        <p className="mt-9 max-w-xl font-body font-light text-[var(--luft-ink-soft)] text-base leading-relaxed">
          Rituais de movimento prontos para o seu ritmo. Novas aulas
          adicionadas todos os meses.
        </p>
      </section>

      {/* Videos */}
      <section className="px-6 sm:px-10 lg:px-16 py-20 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-14 gap-y-20">
          {LIBRARY_VIDEOS.map((v, i) => (
            <MemberVideoCard key={v.id} video={v} index={i} onPlay={playVideo} />
          ))}
        </div>
      </section>

      {/* Footer note */}
      <section className="bg-[var(--luft-bg-alt)] py-20 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[760px] mx-auto text-center">
          <p
            className="text-[var(--luft-ink-soft)] mb-5"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.66rem",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
            }}
          >
            Biblioteca Lu.ft · para os alunos do estúdio
          </p>
          <p className="font-display italic text-[var(--luft-ink)] text-2xl sm:text-3xl leading-snug">
            "Movimento consciente,<br />onde estiver."
          </p>
        </div>
      </section>

      <VideoPlayerModal video={active} onClose={() => setActive(null)} />
    </main>
  );
}

export default function Members() {
  const [member, setMember] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await verifyAccess();
      if (cancelled) return;
      setMember(data);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogout = () => {
    clearToken();
    setMember(null);
    trackEvent("members_logout", {});
    toast("Sessão terminada.");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[var(--luft-bg)] flex items-center justify-center">
        <p
          className="text-[var(--luft-ink-soft)]"
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "0.7rem",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
          }}
        >
          A preparar a sua sessão…
        </p>
      </div>
    );
  }

  if (!member) {
    return <Gate onSuccess={(data) => setMember(data)} />;
  }

  return <Library member={member} onLogout={onLogout} />;
}
